import FormattedObject from "@/models/interfaces/viewer/formatted-object.ts";

export const normalizeViewerObjectId = (value: unknown): string | undefined => {
    if (typeof value === "string" && /^(?:H\d+::H\d+|\d+)$/.test(value)) {
        return value;
    }

    if (typeof value === "number" && Number.isSafeInteger(value) && value >= 0) {
        return String(value);
    }

    return undefined;
};

export const parseViewerObjectId = (value: unknown): FormattedObject | undefined => {
    const id = normalizeViewerObjectId(value);

    if (id === undefined) {
        return undefined;
    }

    const legacyParts = /^H(\d+)::H(\d+)$/.exec(id);

    if (legacyParts) {
        const workspaceUid = Number(legacyParts[1]) - 1;
        const objectUid = Number(legacyParts[2]);

        if (!Number.isSafeInteger(workspaceUid) || !Number.isSafeInteger(objectUid)) {
            return undefined;
        }

        return { workspaceUid, objectUid };
    }

    // Keep the complete identifier, including values beyond Number.MAX_SAFE_INTEGER.
    return { objectUid: id };
};

export const isSameViewerObject = (first: FormattedObject, second: FormattedObject): boolean => {
    // Only legacy identifiers have a parent. Keep both namespaces separate.
    const hasSameParent = first.workspaceUid === second.workspaceUid;
    const hasSameId = String(first.objectUid) === String(second.objectUid);

    return hasSameParent && hasSameId;
};

interface ViewerGeometryModel {
    getItemsIds(): Promise<number[]>;
    getGuidsByLocalIds(ids: number[]): Promise<(string | null)[]>;
}

export const resolveViewerObjectIds = async (
    ids: string[],
    models: Iterable<ViewerGeometryModel>,
): Promise<Map<string, string[]>> => {
    const aliases = new Map<string, Set<string>>();

    ids.forEach(id => {
        aliases.set(id, new Set());
    });

    await Promise.all(Array.from(models).map(async model => {
        const localIds = await model.getItemsIds();
        const guids = await model.getGuidsByLocalIds(localIds);

        guids.forEach(guid => {
            if (guid == null) {
                return;
            }

            aliases.get(guid)?.add(guid);

            // The loaders can still emit Hparent::Hobject for new globally unique IDs.
            // Keep the suffix as text: these identifiers can exceed Number.MAX_SAFE_INTEGER.
            const legacyParts = /^H\d+::H(\d+)$/.exec(guid);
            if (legacyParts) {
                aliases.get(legacyParts[1])?.add(guid);
            }
        });
    }));

    const resolved = new Map<string, string[]>();

    aliases.forEach((guids, id) => {
        if (guids.size === 0) {
            resolved.set(id, [id]);
        } else {
            resolved.set(id, Array.from(guids));
        }
    });

    return resolved;
};
